/**
 * 포장 크기 추정 지침 — 견적서 AI 요청 두 곳(공통 속성·옵션별 차별화 속성)이 같이 쓴다. 의존성 없는 순수 파일.
 *
 * 1688 에 실측 포장값(skuShippingInfoList)이 없으면 AI 가 속성·옵션명으로 포장을 추정한다. 그때 말아서·접어서·감아서
 * 포장하는 상품(휘는 커튼레일)의 옵션 길이 3~8m 를 포장 한 변 3000~8000mm 로 적어, 쿠팡 검증(세 변 합 한도)에서
 * 계열 8건이 통째로 막혔다(2026-10-16 PR261016-5C754F, 1688 816012078484).
 * 상품 목록을 코드에 박지 않는다 — 포장 형태 판단을 같은 AI 호출에 먼저 시킨다.
 * 지침 글만으로는 판단을 자주 건너뛰어(실측 5회 중 4회 8000mm) 응답에 packageForm 칸을 치수 바로 앞에 두어
 * 형태를 먼저 적고 치수를 적게 한다. packageForm 은 응답 검증(validateAttributeResponse 등)이 읽지 않는다 — 버려진다.
 */
/**
 * 포장 형태 판단에 같이 보낼 대표이미지 한 장(http 주소만). 없으면 null — 그때는 글만으로 요청한다.
 * 실측(같은 상품·같은 모델 5회씩): 지침+packageForm 만으로 3m 3/5·8m 4/5 정상, 대표이미지까지 붙이면 10/10 정상.
 */
export function packageImageUrl(product) {
  const p = product || {}
  const main = Array.isArray(p.mainImages) ? p.mainImages[0] : null
  const list = [p.productImage, main && typeof main === 'object' ? main.url : main]
  const hit = list.find(u => typeof u === 'string' && /^https?:\/\//i.test(u))
  return hit || null
}

/**
 * 붙인 이미지 때문에 거절된 오류인지 — 이것만 글만으로 다시 보낸다(시간 초과·요청 한도·서버 오류는 다시 보내지 않고 그대로 던진다).
 * 서버(/api/ai/rocket/gemini)는 오류 문구를 그대로 돌려준다: 이미지 받기 실패(fetch failed) · Gemini 의 이미지 거절
 * (2026-10-16 실측: 깨진 이미지 → «[400 Bad Request] Unable to process input image…», 주소 못 받음 → «fetch failed»).
 */
export function isImageRejectedError(error) {
  const msg = String((error && error.message) || error || '')
  if (/timeout|timed out|ECONNABORTED|429|rate limit|로그인|권한/i.test(msg)) return false
  return /image|mime|fetch failed|이미지/i.test(msg)
}

export const PACKAGE_FORM_FIELD = '"packageForm": "말아서|접어서|감아서|펼친 그대로 중 하나"'

export function packageEstimateGuide() {
  return [
    '- ⚠️ 1688 배송 정보의 skuShippingInfoList를 참고하여 포장 크기/무게 추정 (값이 없으면 속성과 옵션정보를 바탕으로 추정치로 작성)',
    '  답의 포장 크기·무게에는 단위를 붙인다(예: "130*40*50mm", "250g") — 1688 크기 width·length·height 는 cm, skuShippingInfoList 무게는 g · skuShippingDetails 무게는 kg',
    '- 🌀 포장 크기를 적기 전에 먼저 포장 형태를 판단해 응답의 packageForm 에 적으세요: 상품명·속성(유형·재질)·옵션명에 휘어지거나(휨가능·플렉시블·可弯曲·软) 접히거나 감기는 성질이 보이면, 말아서(롤)·접어서·감아서(코일) 포장되는 상품입니다 (예: 휘는 레일, 호스, 케이블, 원단·필름 롤).',
    '  그런 상품의 옵션 길이·폭(예: "3m", "8미터")은 펼친 상품 크기이지 포장 크기가 아닙니다 — 옵션 길이를 mm 로 바꿔 포장 한 변에 넣지 마세요. 말거나 접거나 감은 묶음의 포장 크기로 적고, 길이 옵션이 길어지면 묶음이 굵어지거나 무거워지는 만큼만 키우세요.',
    '  packageForm 이 "말아서"·"접어서"·"감아서"이면 skuUnitBoxDimension 의 어느 변도 옵션 길이를 mm 로 바꾼 값(예: 3m → 3000)이 되어서는 안 됩니다 — 적기 전에 다시 확인하세요.',
    '  휘거나 접히지 않아 펼친 그대로 포장할 수밖에 없는 곧은 막대·판 같은 상품만 packageForm 을 "펼친 그대로"로 하고 옵션 길이를 포장 한 변으로 씁니다.',
    '- 📏 포장 크기는 각 면이 최소 10mm 이상, 부피 최소 1000mm³ 이상이어야 함 (예: 100*100*100)',
  ].join('\n')
}

// 포장값을 mm·g 정수로 바꾼다 — AI 가 "13*4*5cm"·"130.5*40*50"·"0.03kg" 처럼 적으면 예전엔 소수점이면 버리고
// cm 는 떼기만 해서(13*4*5 → 13mm) 값이 비거나 10배 작아졌다(2026-10-16 PR261016-9F5455, 4건 «포장값 없음»).
// 못 읽으면 null — 여기서는 채우지 않는다(빈 값의 하한은 fillLogisticsPackage 가 넣는다).
const DIM_UNIT_MM = { mm: 1, cm: 10, m: 1000 }
// 쿠팡 포장 크기 최소 한 변 — quotationService.isValidBoxDimension·schemaValidator 의 «각 면 10mm 이상»과 같은 값
export const BOX_MIN_SIDE_MM = 10
const WEIGHT_UNIT_G = { g: 1, kg: 1000 }

export function normalizeBoxDimension(dimension) {
  if (typeof dimension === 'number') dimension = String(dimension)
  if (!dimension || typeof dimension !== 'string') return null
  const parts = dimension.trim().toLowerCase().split(/\s*[*x×＊]\s*/)
  if (parts.length !== 3) return null
  const parsed = parts.map(p => /^(\d+(?:\.\d+)?)\s*(mm|cm|m)?$/.exec(p.trim()))
  if (parsed.some(m => !m)) return null
  // 단위를 끝에만 적은 꼴("13*4*5cm")은 그 단위를 세 변 모두에 쓴다. 단위가 하나도 없으면 mm.
  const shared = (parsed.map(m => m[2]).filter(Boolean).pop()) || 'mm'
  const mm = parsed.map(m => Math.round(Number(m[1]) * DIM_UNIT_MM[m[2] || shared]))
  if (mm.some(v => !Number.isFinite(v) || v < 1)) return null
  return mm.join('*')
}

export function normalizeBoxWeight(weight) {
  if (typeof weight === 'number' && Number.isFinite(weight)) weight = String(weight)
  if (!weight || typeof weight !== 'string') return null
  const m = /^(\d+(?:\.\d+)?)\s*(g|kg)?$/.exec(weight.trim().toLowerCase())
  if (!m) return null
  // 단위 없이 1 보다 작은 수(0.05)는 kg 로 읽는다 — 1688 skuShippingDetails 가 kg 로 준다(1g 로 읽으면 50g 상품이 1g)
  const unit = m[2] || (Number(m[1]) < 1 ? 'kg' : 'g')
  const raw = Number(m[1]) * WEIGHT_UNIT_G[unit]
  if (!(raw > 0)) return null
  const g = Math.max(1, Math.round(raw))   // 「0.3g」처럼 단위를 적은 아주 가벼운 값은 1g — 0 은 쿠팡이 거절한다
  // 위 한도를 두지 않는다 — 100kg 넘는다고 null 로 버리면 포장 하한(floorBoxWeight)이 «값 없음»으로 보고 100g 으로 내렸다(#2021 반려)
  return String(g)
}

// 견적서 AI 답 정제 — quotationService.sanitizeBoxDimension·sanitizeBoxWeight 가 부른다. 예전엔 그쪽이 cm·kg 를 떼기만 해서
// 위 normalize 를 못 거쳤다(13*4*5cm → 13mm → 하한 100*100*100, 2kg → 2g · 2026-10-20 201B76 daycozy01).
// 쿠팡 최소(각 면 10mm·부피 1000mm³ — quotationService.isValidBoxDimension 과 같은 값)에 못 미치면 null(저장값을 두고, 없으면 하한).
export function sanitizeAiBoxDimension(dimension) {
  const mm = normalizeBoxDimension(dimension)
  if (!mm) return null
  const sides = mm.split('*').map(Number)
  if (sides.some(v => v < BOX_MIN_SIDE_MM) || sides[0] * sides[1] * sides[2] < 1000) return null
  return mm
}

export function sanitizeAiBoxWeight(weight) {
  return normalizeBoxWeight(weight)
}

// 포장 하한(사장님 #260, 2026-10-17): «100×100×100 이하면 그냥 100×100×100 · 무게는 100g» — 막지 않고 하한값으로 채운다.
// 단위는 쿠팡 skuUnitBoxDimension 과 같은 mm(옛 기본값 100*100*100 도 mm). 1688 이 1cm·1g 같은 가짜 값을 줘도 그대로 나가지 않게 한다.
// 「이하」는 세 변 모두 100mm 이하(= 100×100×100 상자에 들어감)로 판단한다 — 부피로 보면 300*50*50 처럼 긴 상자가 100mm 로 줄어든다.
// 한 변이라도 100mm 를 넘으면 그 크기를 두고, 10mm 보다 작은 변만 10mm 로 올린다(쿠팡 «각 면 10mm 이상»).
// 쿠팡은 단품 포장 세 변 합이 이 값을 넘으면 검증에서 막는다 — tool-exec/rocket-submit.js PACKAGE_SIDE_SUM_MAX_MM 과 한 값(pkgSideLimit.test.mjs 가 대조)
export const PACKAGE_SIDE_SUM_MAX_MM = 2500
export const BOX_FLOOR_SIDE_MM = 100
export const BOX_FLOOR_DIMENSION = '100*100*100'
export const BOX_FLOOR_WEIGHT_G = 100

// 1688 판매자 포장값을 믿지 않는 꼴(사장님 판 #733 ② 10-29 «AI 가 사진 보고 상자 크기 짐작해 채우고 진행» · cpl-agents-boxguess-1):
// 세 변 합이 쿠팡 한도를 넘거나, 두께 ~1cm 에 한 변이 1m 넘는 «펼친 꼴»(PR261028-F3EC5A 300×150×1cm) — 판매자가 상자 대신 펼친 제품 크기를 적었다.
// 30×20×1cm 같은 진짜 얇은 상자(봉투·서류)는 긴 변이 짧아 안 걸린다.
export const FLAT_THIN_MAX_MM = 15
export const FLAT_LONG_MIN_MM = 1000
/** mm 치수('a*b*c')가 믿지 못할 1688 포장값이면 까닭 글, 아니면 null */
export function untrusted1688Box(dimension) {
  const sides = String(dimension || '').split('*').map(Number)
  if (sides.length !== 3 || sides.some(v => !Number.isFinite(v) || v <= 0)) return null
  const sum = sides.reduce((a, b) => a + b, 0)
  if (sum > PACKAGE_SIDE_SUM_MAX_MM) return `세 변 합 ${sum}mm 로 쿠팡 한도 ${PACKAGE_SIDE_SUM_MAX_MM}mm 를 넘음`
  if (Math.min(...sides) <= FLAT_THIN_MAX_MM && Math.max(...sides) >= FLAT_LONG_MIN_MM) {
    return `두께 ${Math.min(...sides)}mm 에 한 변 ${Math.max(...sides)}mm 인 펼친 꼴`
  }
  return null
}

// 비었거나 못 읽으면 하한값. 읽으면 mm 로 바꾼 뒤 하한을 적용한다.
export function floorBoxDimension(dimension) {
  const dim = normalizeBoxDimension(dimension)
  if (!dim) return BOX_FLOOR_DIMENSION
  const sides = dim.split('*').map(Number)
  if (sides.every(v => v <= BOX_FLOOR_SIDE_MM)) return BOX_FLOOR_DIMENSION
  return sides.map(v => Math.max(v, BOX_MIN_SIDE_MM)).join('*')
}

// 비었거나 못 읽으면 100g. 읽으면 g 로 바꾼 뒤 100g 보다 가벼우면 100g.
export function floorBoxWeight(weight) {
  const g = Number(normalizeBoxWeight(weight))
  return String(g > BOX_FLOOR_WEIGHT_G ? g : BOX_FLOOR_WEIGHT_G)
}

const isBlank = v => v === undefined || v === null || String(v).trim() === ''

/**
 * 견적서가 옵션 한 줄의 logisticsPage 에 포장값을 넣는다(quotationService.applyAttributesToOption). lp 를 고친다.
 * - 이미 저장된 값(사람이 견적서 화면에서 넣은 값)은 덮지 않는다. 옛 기본값(100*100*100 / 100·1000)은 저장값으로 보지 않는다.
 * - 새 값(AI·1688)이 있으면 하한을 적용해 넣고, 없으면 하한값(100*100*100 / 100)을 넣는다 — 빈 채로 쿠팡에 null 이 가지 않게.
 * - 칸이 아예 없어도 «값 없음» 으로 보고 채운다(예전엔 건너뛰어 쿠팡 «단품 포장 무게 null» 거절 PR261018-4D1FE4 · 사장님 #260 «막지 말고 채움»).
 * @returns {string[]} 남길 로그 줄
 */
export function fillLogisticsPackage(lp, attributeData) {
  const logs = []
  if (!lp) return logs
  const data = attributeData || {}
  const placeholder = lp.skuUnitBoxDimension === BOX_FLOOR_DIMENSION && ['100', '1000'].includes(String(lp.skuUnitBoxWeight))
  const rules = [
    ['skuUnitBoxDimension', '크기', floorBoxDimension],
    ['skuUnitBoxWeight', '무게', floorBoxWeight],
  ]
  for (const [key, label, floor] of rules) {
    if (!isBlank(lp[key]) && !placeholder) {
      if (data[key] && String(data[key]) !== String(lp[key])) {
        logs.push(`🔒 물류 정보(${label}) 저장값 유지: ${lp[key]} (새 값 ${data[key]} 무시)`)
      }
      continue
    }
    const value = floor(data[key])
    if (!isBlank(data[key]) && String(data[key]) !== value) {
      logs.push(`📦 물류 정보(${label}) 하한 적용: ${data[key]} → ${value}`)
    } else if (isBlank(data[key])) {
      logs.push(`📦 물류 정보(${label}) 값 없음 → 하한값 ${value}`)
    }
    lp[key] = value
    logs.push(`✅ 물류 정보(${label}) 적용: ${value}`)
    // 1688 크기를 버리고 AI 가 짐작한 크기면 «AI 짐작» 표시(판 #733 ②) — rocket-submit oversizePackage 가 추정으로 읽는다 · 하한값은 짐작 아님
    if (key === 'skuUnitBoxDimension' && data.packageSource === 'ai-guess' && !isBlank(data[key])) {
      lp.packageSource = 'ai-guess'
      logs.push(`🤖 물류 정보(크기) AI 짐작: ${value} (1688 판매자 포장값을 펼친 크기로 보고 버림)`)
    }
  }
  return logs
}

/** 옵션 문서 한 줄(docItem)에 logisticsPage 가 없으면 만들고 fillLogisticsPackage 로 채운다 — lp 없음도 «값 없음»(#2115 겹침 · 총괄 답 A) */
export function fillDocLogisticsPackage(docItem, attributeData) {
  if (!docItem.logisticsPage) docItem.logisticsPage = {}
  return fillLogisticsPackage(docItem.logisticsPage, attributeData)
}

// 1688 이 옵션(SKU)별로 준 실측 포장값을 그대로 쓴다 — AI 추정보다 먼저. 옵션 id = 1688 skuId(processOptions).
// skuShippingInfoList 는 cm·g, skuShippingDetails 의 무게는 kg. 0 은 «없음»이라 건너뛰고 그 칸은 AI 가 채운다.
// 묶음 판매(pricing.bundleInfo.hasBundling)이거나 그 옵션의 bundleQuantity > 1 이면 단품 값이 맞지 않으니 쓰지 않는다.
// 1688 이 얇은 봉투를 0.1cm 처럼 주면 1mm 가 돼 «각 면 10mm 이상» 검증(quotationService isValidBoxDimension·schemaValidator)에 걸린다 —
// 그 변만 10mm 로 올리고 note 에 남긴다. 다른 옵션의 크기를 빌려 오지 않는다(PR261016-8140C8).
// @returns {{skuUnitBoxDimension?: string, skuUnitBoxWeight?: string, note?: string}}
export function packageFrom1688(product, option) {
  const out = {}
  if (!product || !option || option.id === undefined || option.id === null) return out
  if (product.pricing && product.pricing.bundleInfo && product.pricing.bundleInfo.hasBundling) return out
  // 그 옵션이 2개 이상 묶음이면(bundleQuantity > 1) 한 상자 크기·무게가 단품과 다르다 — 이 옵션은 AI·묶음 추정에 맡긴다
  if (Number(option.bundleQuantity) > 1) return out
  const psi = product.productShippingInfo || {}
  const id = String(option.id)
  const find = list => (Array.isArray(list) ? list.find(s => s && String(s.skuId) === id) : null)
  const info = find(psi.skuShippingInfoList)
  const detail = find(psi.skuShippingDetails)
  const pos = v => Number(v) > 0
  const dimSrc = [info, detail].find(s => s && pos(s.length) && pos(s.width) && pos(s.height))
  if (dimSrc) {
    const dim = normalizeBoxDimension(`${dimSrc.length}*${dimSrc.width}*${dimSrc.height}cm`)
    if (dim) {
      const sides = dim.split('*').map(Number)
      if (sides.some(v => v < BOX_MIN_SIDE_MM)) {
        out.skuUnitBoxDimension = sides.map(v => Math.max(v, BOX_MIN_SIDE_MM)).join('*')
        out.note = `1688 포장 크기 ${dim}mm 중 ${BOX_MIN_SIDE_MM}mm 보다 작은 변을 ${BOX_MIN_SIDE_MM}mm 로 올림 → ${out.skuUnitBoxDimension}`
      } else {
        out.skuUnitBoxDimension = dim
      }
      // 한도 넘음·펼친 꼴이면 크기를 안 쓴다 → quotationService 가 견적 AI 짐작(사진·packageForm) 값을 쓴다(판 #733 ② · PR261028-F3EC5A).
      // AI 짐작도 한도를 넘으면 rocket-submit packageFailReason 이 «추정한 포장» 갈래(정말 큰 상품)로 말한다. 무게는 펼침과 무관해 1688 값 그대로
      const why = untrusted1688Box(out.skuUnitBoxDimension)
      if (why) {
        out.untrusted = { dim: out.skuUnitBoxDimension, why }
        delete out.skuUnitBoxDimension
        const flat = `1688 판매자 포장값 ${out.untrusted.dim}mm 는 ${why} — 상자 대신 펼친 크기로 보여 쓰지 않음 → AI 짐작(사진·포장 형태)으로 채움`
        out.note = out.note ? `${out.note} · ${flat}` : flat
      }
    }
  }
  const weight = info && pos(info.weight) ? normalizeBoxWeight(`${info.weight}g`)
    : detail && pos(detail.weight) ? normalizeBoxWeight(`${detail.weight}kg`) : null
  if (weight) out.skuUnitBoxWeight = weight
  return out
}

// 쿠팡에 안 보내는 쿠플러스 안 표시(logisticsPage) — tool-exec/rocket-submit.js LOCAL_ONLY_LOGISTICS_KEYS 와 같은 값(tool-exec 시험이 대조)
export const LOCAL_ONLY_LOGISTICS_KEYS = ['packageSource']

/** 화면 등록전송(AIRocketReg.vue → 확장)이 쿠팡에 넘길 본문 — 사본에서 안 표시만 뺀다 · 원본(화면·DB 문서)은 그대로 */
export function coupangDocForSend(doc) {
  if (!doc) return {}
  if (!Array.isArray(doc.jsonDocument)) return doc
  const jsonDocument = doc.jsonDocument.map(d => {
    if (!d || !d.logisticsPage || !LOCAL_ONLY_LOGISTICS_KEYS.some(k => k in d.logisticsPage)) return d
    const logisticsPage = { ...d.logisticsPage }
    for (const k of LOCAL_ONLY_LOGISTICS_KEYS) delete logisticsPage[k]
    return { ...d, logisticsPage }
  })
  return { ...doc, jsonDocument }
}

// 견적 AI 요청 글에 붙이는 1688 배송 정보 — 믿지 못할 크기(untrusted1688Box)는 빼고 경고 한 줄을 그 SKU 에 단다.
// 그대로 주면 AI 가 300×150×1 을 베껴 같은 값이 다시 나온다(판 #733 ② · cpl-agents-boxguess-1). 무게는 남긴다 · 원본은 안 고친다.
export function shippingInfoForAi(productShippingInfo) {
  const psi = productShippingInfo || {}
  const out = { ...psi }
  const pos = v => Number(v) > 0
  const scrub = (list, unit) => list.map(s => {
    if (!s || !(pos(s.length) && pos(s.width) && pos(s.height))) return s
    const dim = normalizeBoxDimension(`${s.length}*${s.width}*${s.height}${unit}`)
    const why = dim ? untrusted1688Box(dim.split('*').map(v => Math.max(Number(v), BOX_MIN_SIDE_MM)).join('*')) : null
    if (!why) return s
    const { length, width, height, ...rest } = s
    return { ...rest, packageSizeWarning: `이 옵션의 1688 포장 크기 ${length}×${width}×${height}${unit} 는 ${why} — 판매자가 적은 펼친 크기라 믿지 말 것. 사진·packageForm 으로 상자 크기를 짐작해 적을 것(AI 짐작)` }
  })
  if (Array.isArray(psi.skuShippingInfoList)) out.skuShippingInfoList = scrub(psi.skuShippingInfoList, 'cm')
  if (Array.isArray(psi.skuShippingDetails)) out.skuShippingDetails = scrub(psi.skuShippingDetails, 'cm')
  return out
}
